import moment from 'moment-timezone'

const STORAGE_KEY_PREFIX = 'agent_chat_first_open_'
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000

function getStorageKey(userId: string, businessId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}_${businessId}`
}

function isValidTimezone(
  timezone: string | null | undefined,
): timezone is string {
  return (
    typeof timezone === 'string' &&
    timezone.trim() !== '' &&
    moment.tz.zone(timezone) != null
  )
}

/**
 * Returns true if we have not yet recorded a "first open" for the current day.
 * Uses calendar day in business timezone when timezone is valid; otherwise
 * falls back to a 12-hour rolling window.
 */
export function isFirstOpenToday(
  userId: string,
  businessId: string,
  timezone: string | null | undefined,
): boolean {
  const key = getStorageKey(userId, businessId)
  const stored =
    typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null

  if (!stored) {
    return true
  }

  if (isValidTimezone(timezone)) {
    const todayInTz = moment.tz(Date.now(), timezone).format('YYYY-MM-DD')
    const storedIsDate = /^\d{4}-\d{2}-\d{2}$/.test(stored)
    if (storedIsDate) {
      return stored !== todayInTz
    }
    return true
  }

  const storedTs = parseInt(stored, 10)
  if (!Number.isNaN(storedTs)) {
    return Date.now() - storedTs >= TWELVE_HOURS_MS
  }

  return true
}

/**
 * Records that the first open of the day has been done for this user/business,
 * so the next call to isFirstOpenToday in the same calendar day (or within
 * 12h in fallback mode) returns false.
 */
export function markFirstOpenDone(
  userId: string,
  businessId: string,
  timezone: string | null | undefined,
): void {
  if (typeof localStorage === 'undefined') return

  const key = getStorageKey(userId, businessId)

  try {
    if (isValidTimezone(timezone)) {
      const todayInTz = moment.tz(Date.now(), timezone).format('YYYY-MM-DD')
      localStorage.setItem(key, todayInTz)
    } else {
      localStorage.setItem(key, Date.now().toString())
    }
  } catch {
    // Ignore quota or other localStorage errors
  }
}
